import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function CommunityPage() {
  // Add scroll to top on mount and set page metadata
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.title = 'Community - Connect with Americans Moving to Ireland | Ireland Bound';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Join our community of Americans relocating to Ireland. Share experiences, ask questions, and connect with others on the same journey.');
    }
  }, []);

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
        <Navbar />

        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto">
            {/* Coming Soon Banner */}
            <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-12 text-center text-white mb-8">
              <div className="max-w-3xl mx-auto">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-rocket-line text-5xl"></i>
                </div>
                <h1 className="text-5xl font-bold mb-4">Community Features Coming Soon!</h1>
                <p className="text-xl text-emerald-50 mb-8">
                  We're building an amazing community platform where you can connect with other Americans moving to Ireland, share experiences, ask questions, and support each other on this journey.
                </p>
                <div className="flex flex-wrap justify-center gap-4 mb-8">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3">
                    <i className="ri-chat-3-line text-2xl mb-2"></i>
                    <p className="font-semibold">Discussion Forums</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3">
                    <i className="ri-group-line text-2xl mb-2"></i>
                    <p className="font-semibold">Interest Groups</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3">
                    <i className="ri-calendar-event-line text-2xl mb-2"></i>
                    <p className="font-semibold">Local Meetups</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3">
                    <i className="ri-user-heart-line text-2xl mb-2"></i>
                    <p className="font-semibold">Mentorship</p>
                  </div>
                </div>
                <p className="text-emerald-100 mb-6">
                  Want to be notified when we launch? Join our newsletter to be the first to know!
                </p>
                <button
                  onClick={() => window.location.href = '/lead-magnets'}
                  className="bg-white text-emerald-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-emerald-50 transition-colors whitespace-nowrap inline-flex items-center gap-2"
                >
                  <i className="ri-mail-line"></i>
                  Get Notified at Launch
                </button>
              </div>
            </div>

            {/* What's Coming */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-discuss-line text-3xl text-emerald-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Discussion Forums</h3>
                <p className="text-gray-600">
                  Ask questions, share experiences, and get advice from Americans who've already made the move to Ireland.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-team-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Interest Groups</h3>
                <p className="text-gray-600">
                  Join groups based on your interests, profession, location, or stage in the relocation process.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-map-pin-user-line text-3xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Local Meetups</h3>
                <p className="text-gray-600">
                  Organize and attend local meetups in Irish cities to make friends and build your network.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-star-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Mentorship Program</h3>
                <p className="text-gray-600">
                  Connect with experienced expats who can guide you through the relocation process.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-book-open-line text-3xl text-pink-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Success Stories</h3>
                <p className="text-gray-600">
                  Read inspiring stories from Americans who've successfully relocated and built new lives in Ireland.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-question-answer-line text-3xl text-cyan-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Q&A Sessions</h3>
                <p className="text-gray-600">
                  Participate in live Q&A sessions with relocation experts, immigration lawyers, and more.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Need Help Right Now?
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                While we're building the community features, chat with our Relocation Expert for personalized guidance
              </p>
              <button
                onClick={() => {
                  const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                  if (widget) widget.click();
                }}
                className="bg-emerald-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-emerald-700 transition-colors whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className="ri-chat-3-line text-xl"></i>
                Talk to Relocation Expert
              </button>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}